import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateAnnualReportFirstAccess } from '@/hooks/mutations/useUpdateAnnualReportFirstAccess';
import { useTourContext } from '@/contexts/TourContext';
import { DynamicTour } from '@/components/Tour/Tour';
import { TourStepConfig } from '@/components/Tour/Tour.types';
import { useParams } from 'react-router-dom';
import { ANNUAL_REPORT_PAGE_PATH } from '@/constants/routes';

export function AnnualReportTour() {
  const { t } = useTranslation();
  const updateAnnualReportFirstAccessMutation = useUpdateAnnualReportFirstAccess();
  const { setTourSteps, endTour } = useTourContext();
  const { id } = useParams();

  const steps: TourStepConfig[] = useMemo(
    () => [
      {
        title: t('annual_report_tour_welcome_step_title'),
        description: t('annual_report_tour_welcome_step_description'),
        isAnchorCard: false,
        staticPosition: {
          bottom: '40px',
          left: '40px',
          showArrow: false
        }
      },
      {
        title: t('annual_report_tour_requisites_step_title'),
        description: t('annual_report_tour_requisites_step_description'),
        isAnchorCard: true,
        targetSelector: '.requisite-item-tour-target',
        elementId: 'tour-btn-banks',
        placement: 'bottom-end',
        navigateToPath: `${ANNUAL_REPORT_PAGE_PATH}/${id}/check_connected_banks`
      },
      {
        description: t('annual_report_tour_banks_reconnect_description'),
        isAnchorCard: true,
        targetSelector: '.tour-connected-banks-wrapper',
        elementId: 'tour-target-bank-btn',
        placement: 'bottom-end',
        prevPath: `${ANNUAL_REPORT_PAGE_PATH}/${id}`,
        navigateToPath: `${ANNUAL_REPORT_PAGE_PATH}/${id}/missing_documents_for_transactions`
      },
      {
        description: t('annual_report_tour_missing_documents_step_description'),
        isAnchorCard: true,
        targetSelector: '.tour-missing-documents-wrapper',
        elementId: 'tour-missing-documents-select',
        placement: 'left',
        prevPath: `${ANNUAL_REPORT_PAGE_PATH}/${id}/check_connected_banks`
      },
      {
        description: t('annual_report_tour_missing_documents_confirm_step_description'),
        isAnchorCard: true,
        targetSelector: '.tour-missing-documents-wrapper',
        elementId: 'tour-missing-documents-confirm',
        placement: 'left',
        navigateToPath: `${ANNUAL_REPORT_PAGE_PATH}/${id}`
      },
      {
        description: t('annual_report_tour_requisites_last_step_description'),
        isAnchorCard: true,
        targetSelector: '.requisites-details-tour-wrapper',
        elementId: 'requisites-fullfilled-btn',
        placement: 'left',
        prevPath: `${ANNUAL_REPORT_PAGE_PATH}/${id}/missing_documents_for_transactions`
      }
    ],
    [t]
  );

  const handleComplete = () => {
    setTourSteps(0);
    endTour();
    updateAnnualReportFirstAccessMutation.mutate();

    document.querySelectorAll('.tour-highlight-element').forEach((el) => {
      el.classList.remove('tour-highlight-element');
    });
  };

  const handleSkip = () => {
    handleComplete();
  };

  return <DynamicTour steps={steps} onComplete={handleComplete} onSkip={handleSkip} />;
}
